import React from 'react'
import { useFormik } from 'formik'
import { GroupMembersSelector } from 'sharedComponents/GroupMembersSelector'
import Button from 'components/Button'
import Card from 'components/Card'
import { Dialog } from 'components/Dialog'
import { MemberFormValues, useAddTestSessionMemberFromGroup } from '@modmd/data'
import Icon from '../../components/Icon/Icon'

interface Props {
  groupId: string
  companyId: string
  testSessionId: string
  onAddSuccess: () => void
}

const AddMembersFromGroup: React.VFC<Props> = ({
  groupId,
  companyId,
  testSessionId,
  onAddSuccess,
}) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [membersPage, setMembersPage] = React.useState(1)
  const {
    addTestSessionMembersFromGroup,
    addTestSessionMembersFromGroupLoading,
  } = useAddTestSessionMemberFromGroup()

  const membersForm = useFormik({
    initialValues: {
      includedIds: [] as string[],
      excludedIds: [] as string[],
      isAllSelected: false,
    },
    onSubmit: async ({ includedIds, excludedIds, isAllSelected }) => {
      try {
        await addTestSessionMembersFromGroup({
          variables: {
            id: testSessionId,
            inputData: {
              groupId,
              includedIds,
              excludedIds,
              isAllSelected,
            },
          },
        })
        onAddSuccess()
      } catch {
        // ignore errore
      }
    },
  })

  const handleAddMember = React.useCallback(
    (user: MemberFormValues) => {
      if (membersForm.values.isAllSelected) {
        void membersForm.setFieldValue(
          'excludedIds',
          membersForm.values.excludedIds?.filter((id) => id !== user.User.id) || []
        )
      } else {
        void membersForm.setFieldValue('includedIds', [
          ...membersForm.values.includedIds,
          user.User.id,
        ])
      }
    },
    [membersForm]
  )

  const handleRemoveMember = React.useCallback(
    (userId: string) => {
      if (membersForm.values.isAllSelected) {
        void membersForm.setFieldValue('excludedIds', [...membersForm.values.excludedIds, userId])
      } else {
        void membersForm.setFieldValue(
          'includedIds',
          membersForm.values.includedIds?.filter((id) => id !== userId) || []
        )
      }
    },
    [membersForm]
  )

  return (
    <>
      <Button onClick={() => setIsDialogOpen(true)} leftIcon={<Icon.Plus size="L" />}>
        Browse group
      </Button>
      <Dialog
        isOpen={!!isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
        variant="right"
        maxWidth="450px"
      >
        <Card.Title onDismiss={() => setIsDialogOpen(false)}>Browse group</Card.Title>
        <Card.Content display="grid" gridGap="0.5rem">
          <GroupMembersSelector
            companyId={companyId}
            groupId={groupId}
            includedUserIds={membersForm.values.includedIds}
            excludedUserIds={membersForm.values.excludedIds}
            page={membersPage}
            isSelectedAll={membersForm.values.isAllSelected}
            onAddMember={handleAddMember}
            onRemoveMember={handleRemoveMember}
            onPageChange={setMembersPage}
            onToggleAll={(isSelectedAll) => {
              void membersForm.setFieldValue('isAllSelected', isSelectedAll)
              void membersForm.setFieldValue(isSelectedAll ? 'excludedIds' : 'includedIds', [])
            }}
          />
          <Button
            type="submit"
            onClick={() => membersForm.handleSubmit()}
            isFetching={addTestSessionMembersFromGroupLoading}
          >
            Submit
          </Button>
        </Card.Content>
      </Dialog>
    </>
  )
}

export { AddMembersFromGroup }
